/**
 * ATL-1020 — one place that answers "does this store fiscalize, and with what?".
 *
 * WHY THIS MODULE EXISTS
 * ----------------------
 * Every read path used to spell "this store does not fiscalize" as
 *
 *     storesDict?.[storeId]?.fiscalOperator?.id !== 'none'
 *
 * which asks whether the id is the literal `'none'` — not whether an operator is configured. A
 * store whose `fiscalOperator` is null answers `undefined !== 'none'` → **true**, i.e. "this store
 * fiscalizes", and everything downstream then acts on an operator that is not there:
 *
 *   - Sales History and Sale Detail put a red «У чека отсутствует фискальный признак» on every
 *     sale the store has ever made, and offered a re-fiscalize button that can do nothing.
 *   - The New Sale screen took the OFD branch instead of the internal-receipt branch, so a store
 *     with `printChecks` on silently stopped printing receipts.
 *   - The delete button in ReturnSaleScreen read `fiscalOperator.id` with no guard at all and
 *     threw `TypeError: null is not an object` (Sentry 7698318886, org PULSE, release 7.1.1) —
 *     the sale could not be deleted.
 *
 * `null` is not a hypothetical shape. The signup payload sends
 * `{id: 'none', text: 'Без фискализации'}` and `StoreSetupDto` accepts it, but
 * `create_organization_atomic` was never given a fiscal-operator parameter, so the field was
 * dropped between the DTO and the INSERT: on 2026-08-29, 145 of 380 live stores held NULL in
 * Supabase while Firestore held `{id: 'none'}` for every single one of them. The writer is fixed
 * alongside this module and the rows are backfilled, but the reader must not depend on that —
 * Firestore docs written before the field existed have no `fiscalOperator` either, and
 * `StoreDetailScreen` has always resolved that same absence to 'none'
 * (`detail?.fiscalOperator || FISCAL_OPERATORS[0]`). This module simply applies those existing
 * semantics on the read paths.
 *
 * WHAT IT DELIBERATELY DOES NOT DO
 * --------------------------------
 * It does not judge whether an operator id is one the app can actually talk to. An id outside
 * {'light-kassa', 'wipon-kassa'} still counts as "fiscalization configured", exactly as before, so
 * a store misconfigured to an unknown operator keeps warning that its receipts are unsigned rather
 * than quietly reporting itself as non-fiscal. Only ABSENT changes meaning here.
 */

/** The operator id a store carries when it does not fiscalize. */
export const FISCAL_OPERATOR_NONE = 'none'
export const LIGHT_KASSA = 'light-kassa'
export const WIPON_KASSA = 'wipon-kassa'

export interface FiscalOperator {
  id?: string | null
  text?: string | null
}

/** The subset of a store these decisions need. Both credential spellings are the stored ones. */
export interface FiscalStore {
  fiscalOperator?: FiscalOperator | null
  /** Light Kassa credentials — capitalised in Firestore, and preserved by the backend transform. */
  LKCredentials?: unknown
  wiponCredentials?: unknown
}

export type MaybeStore = FiscalStore | null | undefined

/**
 * The store's operator id, resolving every shape of "not configured" to `'none'`.
 *
 * Absent store (an id that is not in `storesDict`, or a cashier with no store at all), absent
 * `fiscalOperator`, and an operator object without an `id` all answer `'none'` — never `undefined`,
 * which is the value that used to compare unequal to `'none'` and start the whole failure.
 */
export const getFiscalOperatorId = (store: MaybeStore): string =>
  store?.fiscalOperator?.id || FISCAL_OPERATOR_NONE

/** Whether this store is expected to sign its receipts with a fiscal operator. */
export const isFiscalizationEnabled = (store: MaybeStore): boolean =>
  getFiscalOperatorId(store) !== FISCAL_OPERATOR_NONE

/**
 * The operator object to hand to the write paths (`deleteSale`, `saveSaleReturn`, …), or `null`
 * when the store does not fiscalize. Those functions all guard with `if (fiscalOperator && …)`, so
 * `null` is the answer that makes them skip the OFD round-trip instead of relying on neither
 * operator branch matching.
 */
export const getFiscalOperator = (store: MaybeStore): FiscalOperator | null =>
  isFiscalizationEnabled(store) ? (store?.fiscalOperator ?? null) : null

/**
 * The credentials belonging to THIS store's operator, or `null` when it does not fiscalize.
 *
 * The call sites used to pick credentials with their own inline ternary, and ReturnSaleScreen's
 * read `storesDict?.[sale.store].fiscalOperator === 'ligth-kassa'` — an operator OBJECT compared to
 * a misspelled STRING. That is false for every store on earth, so every Light Kassa return was
 * fiscalized with `wiponCredentials`. Selecting the pair in one place is what keeps an operator
 * from ever being sent another operator's credentials again.
 */
export const getFiscalCredentials = (store: MaybeStore): unknown => {
  switch (getFiscalOperatorId(store)) {
    case LIGHT_KASSA:
      return store?.LKCredentials ?? null
    case WIPON_KASSA:
      return store?.wiponCredentials ?? null
    default:
      return null
  }
}
